import { PropsWithChildren } from "react";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { Combobox, ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { RadioButton } from "@/Common/Components/Inputs/RadioButton";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TimeSelector } from "@/Common/Components/Inputs/TimeSelector";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { ReferalDoctorSearchAutocompleteCustom } from "@/Common/Components/Search/ReferalDoctorSearchAutocompleteCustom";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { Person } from "@/Common/Models/Person/Person";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { DirectoryProfessionalSearchLockingComponent } from "@/ContactDirectory/Components/DirectoryProfessionalSearchLockingComponent";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models/DirectoryHealthCareProvider";
import { DiagnosisResponse } from "@/Patient/Api/MedfarBilling/Dto/Responses/DiagnosisResponse";
import { FacturationTeleconferenceType } from "@/Patient/Api/MedfarBilling/Dto/Responses/FacturationReponse";
import { OtherCodeResponse } from "@/Patient/Api/MedfarBilling/Dto/Responses/OtherCodeResponse";
import { IJMedfarBillingModel } from "@/Patient/Models/JSONInterfaces/MedfarBilling/IJMedfarBillingModel";
import { ContextElementClass } from "@/Patient/Models/MedfarBilling/ContextElementClass";
import { FacturationCode } from "@/Patient/Models/MedfarBilling/FacturationCode";
import { IMedfarBillingModel } from "@/Patient/Models/UI/MedfarBilling/IMedfarBillingModel";
import { MedfarBillingAutoComplete } from "./MedfarBillingAutoComplete";
import { MedfarBillingAutoCompleteOtherCode } from "./MedfarBillingAutoCompleteOtherCode";
import { IMedfarBillingCustomCodeItem, MedfarBillingCustomCodeBox } from "./MedfarBillingCustomCodeBox";
import { MedfarBillingCustomSelectedCodeListBox } from "./MedfarBillingCustomSelectedCodeListBox";
import { MedfarBillingFavoriteCodePopup } from "./MedfarBillingFavoriteCodePopup";

interface IMedfarBillingProps {
    closeTarget?: number;
    Model: IMedfarBillingModel;
    isPatientSubcribed: boolean;
    IsSpecialist: boolean;
    initialState: MedfarBillingState;
}

class MedfarBillingState {
    public Model: IMedfarBillingModel;
    public showOverlay: boolean;

    // Resident
    public AvecResident: boolean;
    public AvecResidentNom: string;
    public AvecResidentLicence: string;
    public withResidentCurrentSelection: Person;

    // MD Congé
    public MDTraitantEnCongeProlonge: boolean;

    // InterventionClinique
    public isInterventionCliniqueChecked: boolean;
    public InterventionCliniqueDurationMin: string;

    public popup: JSX.Element;
}

class MedfarBilling extends BaseReactComponent<IMedfarBillingProps, MedfarBillingState> {
    private renderTopSectionHeight: number = 156;
    private renderMiddleSectionMaxHeight: number = 467;
    private renderBottomSectionHeight: number = 100;
    private headerHeight: number = 40;
    private footerHeight: number = 56;
    private clinicians: Person[];

    constructor(props: IMedfarBillingProps) {
        super(props);

        this.initData();
        this.state = this.props.initialState ?? MedfarBilling.GetDefaultState();
    }

    public static GetDefaultState(): MedfarBillingState {
        const s = new MedfarBillingState();
        s.showOverlay = false;
        s.InterventionCliniqueDurationMin = "25";
        return s;
    }

    public componentDidUpdate(prevProps: IMedfarBillingProps): void {
        if (this.props.Model != prevProps.Model)
            this.mutateState(s => s.Model = this.props.Model);
    }

    public componentWillUpdate(nextProps: IMedfarBillingProps): void {
        if (this.props !== nextProps)
            this.initData();
    }

    public render(): JSX.Element {
        const professionalComboBoxItems = this.props.Model.get("CommProfTypeList").map(x => new ComboBoxDropDownItem(x.ContextCode, x.ContextDescription, 0)).toList();
        const selectedProfCode = this.props.Model.get("CommProfTypeSelected")?.ContextCode;
        const fullscreenMaxHeight = this.renderTopSectionHeight + this.renderMiddleSectionMaxHeight + this.renderBottomSectionHeight + this.headerHeight + this.footerHeight;

        return (
            <div className="fullscreen" style={{ maxHeight: fullscreenMaxHeight + "px" }}>
                <div className="flex-container pad fmp-popup-header" style={{ paddingBottom: "0px", height: this.headerHeight + "px" }}>
                    <div><h3 className="light-font" style={{ marginTop: "-3px", marginBottom: "8px" }}>{cacheStore.GetString("FacturationTitleGENERERNOUVELLEDEMANDE")}</h3></div>
                    <div>
                        <a className="close" onClick={() => this.onCloseClick()}>x</a>
                    </div>
                </div>
                <div className="b-container">
                    {this.state.popup != null &&
                        this.state.popup
                    }

                    {this.state.showOverlay && <div className="p-overlay" />}
                    {this.props.Model.get("IsPopupInfoOpen") && (
                        <MedfarBillingPopup width={560}>
                            <h2>Information</h2>
                            {this.state.popup != null &&
                                this.state.popup
                            }

                            {this.props.Model.get("PopupInfoText") &&
                                <strong>{JSXHelper.BreakStringIntoParagraphs(this.props.Model.get("PopupInfoText"))}</strong>
                            }

                            {this.props.Model.get("IsDateErrorGrossesse") && (
                                <div className="row">
                                    <div className="col-sm-4 col-md-4 col-lg-4 text-right">
                                        <label style={{ marginBottom: "10px" }}>Date des dernières menstruations: </label>
                                    </div>
                                    <div className="col-sm-8 col-md-8 col-lg-8">
                                        <DateSelector
                                            id="dttt01"
                                            date={this.props.Model.get("TempDatePrevuAccouchement")}
                                            onSelectOp={LocalPatientOperations.MedfarBilling.SetDateGrossesse}
                                            dateFormat="yyyy-MM-dd"
                                            disabled={false}
                                            toDayEnabled
                                            todayOperation={LocalPatientOperations.MedfarBilling.TodayClickGrossesse}
                                        />
                                    </div>
                                </div>
                            )}
                            {this.props.Model.get("IsDateErrorCSST") && (
                                <div className="row">
                                    <div className="col-sm-4 col-md-4 col-lg-4 text-right">
                                        <label style={{ marginTop: "10px" }}>Date d&#39; événement CSST: </label>
                                    </div>
                                    <div className="col-sm-8 col-md-8 col-lg-8">
                                        <DateSelector
                                            id="dttt01"
                                            date={this.props.Model.get("TempDateCSST")}
                                            onSelectOp={LocalPatientOperations.MedfarBilling.SetDateCsst}
                                            dateFormat="yyyy-MM-dd"
                                            disabled={false}
                                            toDayEnabled
                                            todayOperation={LocalPatientOperations.MedfarBilling.TodayClickCSST}
                                        />
                                    </div>
                                </div>
                            )}
                            {this.props.Model.get("IsRefDoctorError") && (
                                <div className="row">
                                    <div className="col-sm-4 col-md-4 col-lg-4 text-right">
                                        <label>Médecin Référant: </label>
                                    </div>
                                    <div className="col-sm-6 col-md-6 col-lg-6 " style={{ alignItems: "center" }}>
                                        <FeatureFlagControl
                                            flag={cacheStore.ClinicSettings.EnableContactDirectory}
                                            activeFeature={(
                                                <DirectoryProfessionalSearchLockingComponent
                                                    componentId="add_appt_referal"
                                                    onItemSelected={item => sessionStore.dispatch(
                                                        LocalPatientOperations.MedfarBilling.Referrals_OnChange,
                                                        {
                                                            value: new PersonDropdownItem({ Item: DirectoryHealthCareProvider.toPerson(item.entity as DirectoryHealthCareProvider) })
                                                        }
                                                    )}
                                                    selectedItems={this.props.Model
                                                        ?.get("SelectedReferalDoctor")
                                                        ?.map(p => ({ Name: `${p.Name}${p.Licence ? ` (${p.Licence}) ` : ""}` }))
                                                        .toArray() ?? []
                                                    }
                                                    searchText={this.props.Model.get("ReferalDoctorSearchString")}
                                                    onTextChange={text => sessionStore.dispatch(LocalPatientOperations.MedfarBilling.Referrals_OnTextChange, { value: text })}
                                                    onRemoveItem={() => sessionStore.dispatch(LocalPatientOperations.MedfarBilling.Referrals_OnClear)}
                                                    dataCy="contactDirectory-medfarBillingReferralSearch"
                                                />
                                            )}
                                            fallbackFeature={(
                                                <div style={{ alignItems: "center", display: "flex" }}>
                                                    <ReferalDoctorSearchAutocompleteCustom
                                                        LicenceOrganisationRuleId="B8E25E9F-6618-4519-9EA3-165D8C54905A"
                                                        useblurevent
                                                        focusOnLoad={false}
                                                        disabled={false}
                                                        displayList
                                                        componentId="add_appt_referal"
                                                        selectedDoctors={this.props.Model.get("SelectedReferalDoctor")}
                                                        selectionOp={LocalPatientOperations.MedfarBilling.Referrals_OnChange}
                                                        clearOp={LocalPatientOperations.MedfarBilling.Referrals_OnClear}
                                                        closeDropDownAfterSelection
                                                        changedTextValueEvent={LocalPatientOperations.MedfarBilling.Referrals_OnTextChange}
                                                        defaultTextValue={this.props.Model.get("ReferalDoctorSearchString")}
                                                    />
                                                </div>
                                            )}
                                        />
                                    </div>
                                </div>
                            )}
                            {this.props.Model.get("CommSpecErrorMessage") && (
                                <div className="row">
                                    <div className="col-sm-4 col-md-4 col-lg-4 text-right">
                                        <label>Spec.Comm.: </label>
                                    </div>
                                    <div className="col-sm-6 col-md-6 col-lg-6 " style={{ alignItems: "center" }}>
                                        <FeatureFlagControl
                                            flag={cacheStore.ClinicSettings.EnableContactDirectory}
                                            activeFeature={(
                                                <DirectoryProfessionalSearchLockingComponent
                                                    componentId="add_appt_referal"
                                                    onItemSelected={item => sessionStore.dispatch(
                                                        LocalPatientOperations.MedfarBilling.CommSpec_OnChange,
                                                        {
                                                            value: new PersonDropdownItem({ Item: DirectoryHealthCareProvider.toPerson(item.entity as DirectoryHealthCareProvider) })
                                                        }
                                                    )}
                                                    selectedItems={this.props.Model
                                                        ?.get("SelectedCommSpec")
                                                        ?.map(p => ({ Name: `${p.Name}${p.Licence ? ` (${p.Licence}) ` : ""}` }))
                                                        .toArray() ?? []
                                                    }
                                                    searchText={this.props.Model.get("CommSpecSearchString")}
                                                    onTextChange={text => sessionStore.dispatch(LocalPatientOperations.MedfarBilling.CommSpec_OnTextChange, { value: text })}
                                                    onRemoveItem={() => sessionStore.dispatch(LocalPatientOperations.MedfarBilling.CommSpec_OnClear)}
                                                    dataCy="contactDirectory-medfarBillingCommSpecSearch"
                                                />
                                            )}
                                            fallbackFeature={(
                                                <div style={{ alignItems: "center", display: "flex" }}>
                                                    <ReferalDoctorSearchAutocompleteCustom
                                                        LicenceOrganisationRuleId="B8E25E9F-6618-4519-9EA3-165D8C54905A"
                                                        useblurevent
                                                        focusOnLoad={false}
                                                        disabled={false}
                                                        displayList
                                                        componentId="add_appt_referal"
                                                        selectedDoctors={this.props.Model.get("SelectedCommSpec")}
                                                        selectionOp={LocalPatientOperations.MedfarBilling.CommSpec_OnChange}
                                                        clearOp={LocalPatientOperations.MedfarBilling.CommSpec_OnClear}
                                                        closeDropDownAfterSelection
                                                        changedTextValueEvent={LocalPatientOperations.MedfarBilling.CommSpec_OnTextChange}
                                                        defaultTextValue={this.props.Model.get("CommSpecSearchString")}
                                                    />
                                                </div>
                                            )}
                                        />
                                    </div>
                                </div>
                            )}
                            {this.props.Model.get("CommProfErrorMessage") && (
                                <div className="">
                                    <div className="row">
                                        <div className="col-sm-3 col-md-3 col-lg-3 text-right">
                                            <label>
                                                Prénom:
                                            </label>
                                        </div>
                                        <div className="col-sm-9 col-md-9 col-lg-9">
                                            <input style={{ width: "200px" }} type="text" value={this.props.Model.get("CommProfFirstName")} onChange={(e: any) => this.onCommProfFirstNameChange(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="row" style={{ marginTop: "10px" }}>
                                        <div className="col-sm-3 col-md-3 col-lg-3 text-right">
                                            <label>
                                                Nom:
                                            </label>
                                        </div>
                                        <div className="col-sm-9 col-md-9 col-lg-9">
                                            <input type="text" style={{ width: "200px" }} value={this.props.Model.get("CommProfLastName")} onChange={(e: any) => this.onCommProfLastNameChange(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="row" style={{ marginTop: "10px" }}>
                                        <div className="col-sm-3 col-md-3 col-lg-3 text-right">
                                            <label style={{ marginTop: "10px", marginBottom: "10px" }}>
                                                Type:
                                            </label>
                                        </div>
                                        <div className="col-sm-9 col-md-9 col-lg-9">
                                            <Combobox id="ktype_" items={professionalComboBoxItems} selectedCode={selectedProfCode} operation={LocalPatientOperations.MedfarBilling.OnCommProfTypeSelection} disabled={false} />
                                        </div>
                                    </div>
                                    <div className="row" style={{ marginTop: "10px" }}>
                                        <div className="col-sm-4 col-md-4 col-lg-4 text-right" />
                                        <div className="col-sm-8 col-md-8 col-lg-8">
                                            <label style={{ marginRight: "2px" }}>
                                                Québec
                                                <input type="radio" checked={this.props.Model.get("CommProfQuebec")} onClick={() => this.onCommProfQuebecClicked()} />
                                            </label>
                                            <label style={{ marginRight: "2px" }}>
                                                Hors Québec
                                                <input type="radio" checked={this.props.Model.get("CommProfOutQuebec")} onClick={() => this.onCommProfOutQuebecClicked()} />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {this.props.Model.get("PatientPECError") && (
                                <div>
                                    <div className="radio">
                                        <label style={{ marginRight: "2px" }}>
                                            <input type="radio" name="n1" checked={this.props.Model.get("IsForfaitAucunCodeChecked")} onClick={() => this.onPECRadioCheck(0)} />
                                            --
                                        </label>
                                    </div>
                                    <div className="radio">
                                        <label style={{ marginRight: "2px" }}>
                                            <input type="radio" name="n1" checked={this.props.Model.get("Is19957CodeChecked")} onClick={() => this.onPECRadioCheck(19957)} />
                                            19957 - Forfait PEC, Sans MD Famille, Non Vulnérable
                                        </label>
                                    </div>
                                    <div className="radio">
                                        <label style={{ marginRight: "2px" }}>
                                            <input type="radio" name="n1" checked={this.props.Model.get("Is19958CodeChecked")} onClick={() => this.onPECRadioCheck(19958)} />
                                            19958 - Forfait PEC, Sans MD Famille, Vulnérable, Non Référé
                                        </label>
                                    </div>
                                    <div className="radio">
                                        <label style={{ marginRight: "2px" }}>
                                            <input type="radio" name="n1" checked={this.props.Model.get("Is19959CodeChecked")} onClick={() => this.onPECRadioCheck(19959)} />
                                            19959 - Forfait PEC, Sans MD Famille, Vunérable, Référé (GAMF)
                                        </label>
                                    </div>
                                    <div className="radio">
                                        <label style={{ marginRight: "2px" }}>
                                            <input type="radio" name="n1" checked={this.props.Model.get("Is19960CodeChecked")} onClick={() => this.onPECRadioCheck(19960)} />
                                            19960 - Forfait PEC, Sans MD Famille, Très Vulnérable, Priorisé et Référé (GAMF)
                                        </label>
                                    </div>
                                </div>
                            )}
                            {this.props.Model.get("Mesure1Error") && (
                                <div style={{ marginLeft: "80px", marginTop: "30px" }}>
                                    <label>
                                        <input type="text" style={{ width: "50px", marginRight: "5px" }} value={this.props.Model.get("TempFacturationCode").Mesure1} onChange={(e: any) => this.onMesure1TextChange(e.target.value)} />
                                        {this.props.Model.get("TempFacturationCode").UnitType1} - {this.props.Model.get("TempFacturationCode").UnitLabel1}
                                    </label>
                                </div>
                            )}
                            {this.props.Model.get("Mesure2Error") && (
                                <div style={{ marginLeft: "80px" }}>
                                    <label>
                                        <input type="text" style={{ width: "50px", marginRight: "5px" }} value={this.props.Model.get("TempFacturationCode").Mesure2} onChange={(e: any) => this.onMesure2TextChange(e.target.value)} />
                                        {this.props.Model.get("TempFacturationCode").UnitType2} - {this.props.Model.get("TempFacturationCode").UnitLabel2}
                                    </label>
                                </div>
                            )}
                            <div className="text-right" style={{ marginTop: "30px" }}>
                                <button
                                    className="btn btn-default"
                                    style={{ marginRight: "5px" }}
                                    onClick={() => this.onCancelButtonClick()}
                                >
                                    {cacheStore.GetString("CancelLabel")}
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => this.onContinueButtonClick()}
                                >
                                    {cacheStore.GetString("ContinueLabel")}
                                </button>
                            </div>

                        </MedfarBillingPopup>
                    )}
                    {this.props.Model.get("FavoritePopupOpen") &&
                        <MedfarBillingFavoriteCodePopup onCloseCallback={() => this.onCancelFavouriteClick()} IsSpecialist={this.props.IsSpecialist} />
                    }
                    {this.props.Model.get("NoSRVorRDVCheckedError") && (
                        <MedfarBillingPopup width={560}>
                            <h2>Spécifier le type de visite</h2>
                            <div className="flex-container" style={{ width: "100%", justifyContent: "center" }}>
                                <div className="flex-container" style={{ marginTop: "35px", width: "200px" }}>
                                    <RadioButton
                                        items={["SRV", "RDV"].map(x => new KeyValuePair(x, x)).toImmutable()}
                                        mainDivCSS=""
                                        radioItemStyle={{ width: "100px" }}
                                        radioInputStyle={{ marginLeft: "-16px" }}
                                        radioInputGroupId="_clientSrvMedfarBillingRadionButtonPopup"
                                        onChange={item => sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnSrvClick, { code: item.Key })}
                                        selectedItem={this.props.Model.get("IsSRVChecked") == true ? "SRV" : this.props.Model.get("IsRDVChecked") == true ? "RDV" : ""}
                                    />
                                </div>
                            </div>
                            <div className="text-right" style={{ marginTop: "25px" }}>
                                <button
                                    className="btn btn-default"
                                    onClick={() => this.onCancelRDVorSRVClick()}
                                    style={{ marginRight: "5px" }}
                                >
                                    {cacheStore.GetString("CancelLabel")}
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => this.generateNewBill()}
                                >
                                    {cacheStore.GetString("FacturationbtnGENERER")}
                                </button>
                            </div>
                        </MedfarBillingPopup>
                    )}

                    {this.renderTopSection()}
                    {this.renderMiddleSection()}
                    {this.renderBottomSection()}
                </div>

                <div className="flex-container footer" style={{ height: this.footerHeight + "px" }}>
                    {!this.props.Model.get("Model").BatchId && (
                        <div>
                            {!this.props.Model.get("IsUpdating") && (
                                <button className="btn btn-primary" onClick={() => this.generateNewBill()}>
                                    <span>{cacheStore.GetString("FacturationbtnGENERER")}</span>
                                </button>
                            )}
                            {this.props.Model.get("IsUpdating") && (
                                <button className="btn btn-primary" onClick={() => this.generateNewBill()}>
                                    <span>{cacheStore.GetString("UpdateLabel")}</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private renderTopSection(): JSX.Element {
        let itemList: IMedfarBillingCustomCodeItem[] = [];
        if (this.props.Model.get("DiagnosisList")?.size > 0) {
            this.props.Model.get("DiagnosisList").map((item: DiagnosisResponse) => {
                itemList.push({
                    key: item.CodeCIM,
                    value: item.DescriptionLongue
                });
            });
        }

        const siteName = cacheStore.GetSiteById(this.props.Model.get("SiteId"))?.Model.ClinicalName;

        return (
            <div className="flex-container pad top" style={{ paddingBottom: "0px", paddingTop: "2px", height: this.renderTopSectionHeight + "px" }}>
                <div className="mc a">
                    <div className="flex-container vpad mid-align" style={{ width: "770px", justifyContent: "flex-start" }}>
                        <div style={{ width: "80px", paddingRight: "17px", textAlign: "right" }}>
                            <label>Date/heure</label>
                        </div>
                        <div className="flex-container" style={{ width: "200px" }}>
                            <DateSelector
                                id="dttt01_BillDate"
                                date={this.props.Model.get("BillDate")}
                                onSelectOp={LocalPatientOperations.MedfarBilling.ChangeDate}
                                dateFormat="yyyy-MM-dd"
                                disabled={false}
                                toDayEnabled
                                todayOperation={LocalPatientOperations.MedfarBilling.TodayClick}
                                isFromMedfarBilling
                            />
                        </div>
                        <div className="flex-container" style={{ marginLeft: "10px", width: "100px" }}>
                            <TimeSelector
                                id="time_001_BillTime"
                                onSelectOp={LocalPatientOperations.MedfarBilling.HoursChange}
                                date={this.props.Model.get("BillDate")}
                                disabled={false}
                            />
                        </div>
                        <div style={{ marginLeft: "15px", whiteSpace: "nowrap" }}>
                            <span>{"(" + siteName + ")"}</span>
                        </div>
                    </div>
                    <div className="flex-container vpad mid-align" style={{ paddingBottom: "2px", paddingTop: "2px", justifyContent: "flex-start" }}>
                        <div style={{ width: "80px" }}>
                            <label>{cacheStore.GetString("DiagnosisLabel")}</label>
                        </div>
                        <div style={{ width: "313px" }}>
                            <MedfarBillingAutoComplete
                                style={{}}
                                serverOperation={ServerOperations.Server.MedfarBilling_SearchDiagnosis}
                                onItemSelectCallback={item => this.onDiagnosisItemSelect(item)}
                            />
                            <div className="flex-container vpad" style={{ paddingTop: "0px" }}>
                                <MedfarBillingCustomCodeBox
                                    items={Immutable.List(itemList)}
                                    onItemRemoveCallback={item => this.onRemoveItemFromDiagnosis(item)}
                                />
                            </div>
                        </div>
                        <div>
                            <div className="checkbox" style={{ marginTop: "7px", marginBottom: "8px", marginLeft: "10px", width: "150px" }}>
                                <label>
                                    <input type="checkbox" checked={this.props.Model.get("SceanceDiffChecked")} onClick={() => this.onSceanceDiffClick()} />
                                    Scéance différente
                                </label>
                            </div>
                            <div className="checkbox" style={{ marginTop: "7px", marginBottom: "8px", marginLeft: "10px" }}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={this.props.Model.get("IsConferenceCallChecked")}
                                        onClick={() => LocalEvent.Dispatch(LocalPatientOperations.MedfarBilling.ConferenceCallClick)}
                                    />
                                    {cacheStore.GetString("TeleconsultationVia")}:
                                </label>
                            </div>
                            <div>
                                <div style={{ marginRight: "18px" }}>
                                    <RadioButton
                                        items={Immutable.List([
                                            new KeyValuePair(cacheStore.GetString("TelephoneLabel"), FacturationTeleconferenceType.Telephone),
                                            new KeyValuePair(cacheStore.GetString("Videoconference"), FacturationTeleconferenceType.Videoconference)
                                        ])}
                                        radioInputGroupId="_clientTeleconsultationType"
                                        selectedItem={this.props.Model.get("TeleconferenceType")}
                                        onChange={item => sessionStore.dispatch(LocalPatientOperations.MedfarBilling.ConferenceTypeSet, { value: item.Value })}
                                        disabled={!this.props.Model.get("IsConferenceCallChecked")}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mc b" style={{ marginRight: "50px" }}>
                    <div>
                        <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                            <div className="text-right" style={{ marginRight: "5px", width: "160px" }}><label>Type de la visite: </label></div>
                            <div className="flex-container" style={{ width: "250px" }}>
                                <RadioButton
                                    items={["SRV", "RDV"].map(x => new KeyValuePair(x, x)).toImmutable()}
                                    mainDivCSS=""
                                    radioItemStyle={{ width: "60px" }}
                                    radioInputStyle={{ marginLeft: "-16px" }}
                                    radioInputGroupId="_clientSrvMedfarBillingRadionButton"
                                    onChange={item => sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnSrvClick, { code: item.Key })}
                                    selectedItem={this.props.Model.get("IsSRVChecked") == true ? "SRV" : this.props.Model.get("IsRDVChecked") == true ? "RDV" : ""}
                                />
                                <div className="radio" style={{ width: "83px" }}>
                                    <label><input type="radio" name="_r1" onClick={() => { }} checked={this.props.Model.get("Is80YearsOld")} disabled />{"> 80"}</label>
                                </div>
                            </div>
                        </div>

                        <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                            <div className="text-right" style={{ marginRight: "5px", width: "160px" }}><label>Nombre d'inscriptions</label></div>
                            <div className="flex-container" style={{ width: "250px" }}>
                                <RadioButton
                                    items={["< 500", "> 500"].map(x => new KeyValuePair(x, x)).toImmutable()}
                                    mainDivCSS=""
                                    radioItemStyle={{ width: "60px" }}
                                    radioInputStyle={{ marginLeft: "-16px" }}
                                    radioInputGroupId="_clientSubscriberNumberMedfarBillingRadioButton"
                                    onChange={item => sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnLessThan500HeaderClick, { code: item.Key })}
                                    selectedItem={this.props.Model.get("IsLessThan500") == true ? "< 500" : this.props.Model.get("IsMoreThan500") == true ? "> 500" : ""}
                                />

                                <div className="radio" style={{ width: "83px" }}>
                                    <label><input type="radio" onClick={() => { }} checked={this.props.Model.get("IsPatientSubscribed")} disabled />
                                        {this.props.Model.get("IsVulnerable") != true ? "Inscrit" : "Inscr. Vul."}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="flex-container" style={{ justifyContent: "center" }}>
                            <div
                                className="flex-container"
                                style={{ justifyContent: "flex-start" }}
                                onClick={() => this.mutateState(s => s.MDTraitantEnCongeProlonge = !this.state.MDTraitantEnCongeProlonge)}
                            >
                                <div className="text-right pointer" style={{ marginRight: "5px", width: "160px", whiteSpace: "nowrap" }}>
                                    <label>MD Trait. Congé Prolongé</label>
                                </div>
                                <div className="pointer">
                                    <CheckboxControl
                                        checked={this.state.MDTraitantEnCongeProlonge}
                                    />
                                </div>
                            </div>
                            <div
                                className="flex-container"
                                style={{ justifyContent: "flex-start", marginLeft: "40px" }}
                                onClick={() => this.onWithResidentClick()}
                            >
                                <div className="text-right pointer" style={{ marginRight: "5px" }}><label>Avec Résident</label></div>
                                <div className="pointer">
                                    <CheckboxControl checked={this.state.AvecResident == true} label="" onChange={() => null} />
                                </div>
                            </div>

                        </div>
                    </div>

                    <div style={{ overflowY: "auto", maxHeight: "48px", marginTop: "5px", height: (this.renderTopSectionHeight - 4 * 24) + "px" /* 4 times the 24px line abobe it */ }}>
                        {this.props.Model.get("MedecinReferant") && (
                            <div className="flex-container" style={{ padding: "0px", justifyContent: "flex-start" }}>
                                <div className="text-right" style={{ marginRight: "5px", whiteSpace: "nowrap", width: "90px" }}>
                                    <strong>MD réf.</strong>
                                </div>
                                <div className="flex-container mid-align">
                                    <label>
                                        {this.props.Model.get("MedecinReferant").Name} ({this.props.Model.get("MedecinReferant").Licence})
                                        <span style={{ marginLeft: "2px" }} onClick={() => this.removeRefDoctor()} className="glyphicon glyphicon-remove red-text pointer" />
                                    </label>
                                </div>
                            </div>
                        )}
                        {this.props.Model.get("CommSpecDoctor") && (
                            <div className="flex-container" style={{ padding: "0px", justifyContent: "flex-start" }}>
                                <div className="text-right" style={{ marginRight: "5px", whiteSpace: "nowrap", width: "90px" }}>
                                    <strong>Spec.Comm.</strong>
                                </div>
                                <div className="flex-container mid-align">
                                    <label>
                                        {this.props.Model.get("CommSpecDoctor").Name} ({this.props.Model.get("CommSpecDoctor").Licence})
                                        <span style={{ marginLeft: "2px" }} onClick={() => this.removeCommSpecDoctor()} className="glyphicon glyphicon-remove red-text pointer" />
                                    </label>
                                </div>
                            </div>
                        )}
                        {this.props.Model.get("ShowCommProf") && (
                            <div className="flex-container" style={{ padding: "0px", justifyContent: "flex-start" }}>
                                <div className="text-right" style={{ marginRight: "5px", whiteSpace: "nowrap", width: "90px" }}>
                                    <strong>Prof.Comm.</strong>
                                </div>
                                <div className="flex-container mid-align">
                                    <label>
                                        {this.props.Model.get("CommProfLastName")}, {this.props.Model.get("CommProfFirstName")} ({this.props.Model.get("CommProfTypeSelected").ContextDescription})
                                        <span style={{ marginLeft: "2px" }} onClick={() => this.removeCommProfDoctor()} className="glyphicon glyphicon-remove red-text pointer" />
                                    </label>
                                </div>
                            </div>
                        )}
                        {this.state.AvecResident == true && (
                            <div className="flex-container" style={{ padding: "0px", justifyContent: "flex-start" }}>
                                <div className="text-right" style={{ marginRight: "5px", whiteSpace: "nowrap", width: "90px" }}>
                                    <strong>Resident.</strong>
                                </div>
                                <div className="flex-container mid-align">
                                    <label>
                                        {this.state.AvecResidentNom} ({this.state.AvecResidentLicence})
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                    {/* end of mc b */}
                </div>
            </div>

        );
    }

    private renderMiddleSection(): JSX.Element {
        // Top 40
        // Header 150
        // Comment Section = 90px
        // footer 52
        // ~= 340
        const differenceBetweenFullScreenAndThisSection = this.renderTopSectionHeight + this.headerHeight + this.renderBottomSectionHeight + this.footerHeight;

        return (
            <div className="fullscreen" data-heightCorrection={-differenceBetweenFullScreenAndThisSection} style={{ overflowY: "auto", maxHeight: this.renderMiddleSectionMaxHeight + "px" }}>
                <div className="flex-container pad middle" style={{ maxWidth: "1270px", paddingTop: "0px", paddingBottom: "0px" }}>
                    <div className="mc a">
                        {this.renderMidA()}
                    </div>
                    <div className="mc b">
                        {this.renderMidB()}
                    </div>
                    <div className="mc c">
                        {this.renderMidC()}
                    </div>
                    <div className="mc d">
                        {this.renderMidD()}
                    </div>
                </div>
            </div>
        );
    }

    private renderBottomSection(): JSX.Element {
        return (
            <div className="flex-container pad bottom" style={{ paddingTop: "5px", height: this.renderBottomSectionHeight + "px" }}>
                <div className="mc a">
                    <textarea className="form-control vresize" rows={3} value={this.props.Model.get("Comments")} onChange={(e: any) => this.onCommentsChange(e.target.value)} placeholder="Commentaires" />
                </div>
                <div className="mc b">
                    <MedfarBillingCustomSelectedCodeListBox
                        style={{ height: "100%" }}
                        items={this.props.Model.get("CodesList")}
                        onItemRemoveCallback={item => this.onRemoveItemFromCodesList(item)}
                    />
                </div>
            </div>
        );
    }

    private renderMidA(): JSX.Element {
        let patientInscritRender = null;
        let patientInscritList = this.props.Model.get("RdvPatientInscritItem");
        if (patientInscritList) {
            patientInscritRender = Object.keys(patientInscritList).map((key: string) => (
                <div className="checkbox" style={{ marginLeft: "5px", marginTop: "0px", marginBottom: "8px" }} key={key}>
                    <label>
                        <input type="checkbox" checked={patientInscritList[key].IsChecked} onClick={() => this.onRdvCheckBoxClick(patientInscritList[key])} />
                        {patientInscritList[key].Description}
                    </label>
                </div>
            ));
        }

        let grossesseRender = null;
        let grossesseList = this.props.Model.get("GrossesseItems");
        if (grossesseList) {
            grossesseRender = Object.keys(grossesseList).map((key: string) => (
                <div className="checkbox" style={{ marginLeft: "5px", marginTop: "0px", marginBottom: "8px" }} key={key}>
                    <label>
                        <input type="checkbox" checked={grossesseList[key].IsChecked} onClick={() => this.onGrossesseCheckboxClick(grossesseList[key])} />
                        {grossesseList[key].Description}
                    </label>
                </div>
            ));
        }

        let visiteADomicileRender = null;
        let vaDomicileList = this.props.Model.get("DomicilesItems");
        if (vaDomicileList) {
            visiteADomicileRender = Object.keys(vaDomicileList).map((key: string) => (
                <div className="checkbox" style={{ marginLeft: "5px", marginTop: "0px", marginBottom: "8px" }} key={key}>
                    <label>
                        <input type="checkbox" checked={vaDomicileList[key].IsChecked} onClick={() => this.onVADomicileCheckboxClick(vaDomicileList[key])} />
                        {vaDomicileList[key].Description}
                    </label>
                </div>
            ));
        }

        let rdvPatientNonInscritRender = null;
        let rdvPatientNonInscritList = this.props.Model.get("SrvNonInscritItem");
        if (rdvPatientNonInscritList) {
            rdvPatientNonInscritRender = Object.keys(rdvPatientNonInscritList).map((key: string) => (
                <div className="checkbox" style={{ marginLeft: "5px", marginTop: "0px", marginBottom: "8px" }} key={key}>
                    <label>
                        <input type="checkbox" checked={rdvPatientNonInscritList[key].IsChecked} onClick={() => this.onSrvPatientNonInscritCheckboxClick(rdvPatientNonInscritList[key])} />
                        {rdvPatientNonInscritList[key].Description}
                    </label>
                </div>
            ));
        }

        return (
            <div>
                <h4 style={{ marginTop: "10px", marginBottom: "0px" }}>RDV - Patient inscrit</h4>
                {patientInscritRender}

                <h4 style={{ marginTop: "10px", marginBottom: "0px" }}>SRV ou patient non-inscrit</h4>
                {rdvPatientNonInscritRender}

                <h4 style={{ marginTop: "10px", marginBottom: "0px" }}>Grossesse</h4>
                {grossesseRender}

                <h4 style={{ marginTop: "10px", marginBottom: "0px" }}>Visite à domicile</h4>
                {visiteADomicileRender}

            </div>
        );
    }

    private renderMidB(): JSX.Element {
        let autreItemsRender = null;
        let autreItemsList = this.props.Model.get("AutresItems");
        if (autreItemsList) {
            autreItemsRender = Object.keys(autreItemsList).map((key: string) => (
                <div className="checkbox" style={{ marginLeft: "5px", marginTop: "0px", marginBottom: "1px" }} key={key}>
                    <label>
                        <input type="checkbox" checked={autreItemsList[key].IsChecked} onClick={() => this.onAutreCheckboxClick(autreItemsList[key])} />
                        {autreItemsList[key].Description}
                    </label>
                </div>
            ));
        }

        let evaluationRefereeRender = null;
        let evaluationRefereeList = this.props.Model.get("EvaluationRefereeItems");
        if (evaluationRefereeList) {
            evaluationRefereeRender = Object.keys(evaluationRefereeList).map((key: string) => (
                <div className="checkbox" style={{ marginLeft: "5px", marginTop: "0px", marginBottom: "1px" }} key={key}>
                    <label>
                        <input type="checkbox" checked={evaluationRefereeList[key].IsChecked} onClick={() => this.onEvalReferreCheckboxClick(evaluationRefereeList[key])} />
                        {evaluationRefereeList[key].Description}
                    </label>
                </div>
            ));
        }

        let ContextComboBoxItems = this.props.Model.get("ContextList").map(x => new ComboBoxDropDownItem(x.ContextCode, x.ContextDescription, 0)).toList();

        return (
            <div>
                <h4 style={{ marginTop: "10px", marginBottom: "0px" }}>Autres</h4>

                <div id="InterventionCliniqueDiv" style={{ width: "270px", marginBottom: "7px" }}>
                    <div
                        className="flex-container pointer"
                        style={{ justifyContent: "flex-start" }}
                        onClick={() => this.mutateState(s => s.isInterventionCliniqueChecked = this.state.isInterventionCliniqueChecked != true, () => this.onIntClinChange())}
                    >

                        <div style={{ marginLeft: "5px" }}>
                            <CheckboxControl checked={this.state.isInterventionCliniqueChecked == true} onChange={() => null} label="" />
                        </div>
                        <div style={{ marginLeft: "5px" }}>Intervention Clinique</div>
                    </div>
                    {this.state.isInterventionCliniqueChecked == true && (
                        <div style={{ marginLeft: "10px" }}>

                            <div className="flex-container" style={{ justifyContent: "flex-start", width: "auto" }}>
                                <div>{cacheStore.GetString("FromLabel") + ":"}</div>
                                <div style={{ marginLeft: "3px" }}>{this.props.Model.get("BillDate").toMoment().format("HH:mm")}</div>
                                <div style={{ marginLeft: "3px" }}>{cacheStore.GetString("ToLabel")}</div>
                                <div style={{ marginLeft: "3px" }}>{this.props.Model.get("BillDate").addMinutes(Number(this.state.InterventionCliniqueDurationMin)).toMoment().format("HH:mm")}</div>

                                <div style={{ marginLeft: "15px" }}>{cacheStore.GetString("DurationLabel") + " (min):"}</div>
                                <TextboxControl
                                    value={this.state.InterventionCliniqueDurationMin}
                                    style={{ marginTop: "-4px", height: "28px", width: "52px" }}
                                    IsNumeric
                                    onChange={value =>
                                        this.mutateState(
                                            s => {
                                                s.InterventionCliniqueDurationMin = value;
                                            },
                                            () => this.onIntClinChange()
                                        )
                                    }
                                />
                            </div>
                            <div className="flex-container" style={{ justifyContent: "flex-start", marginTop: "6px" }}>
                                <div style={{ width: "50px" }}>Contexte:</div>
                                <div style={{ marginLeft: "5px", marginTop: "-5px" }}>
                                    <Combobox id="kstart_" style={{ height: "26px", padding: "5px 10px" }} items={ContextComboBoxItems} selectedCode={this.props.Model.get("SelectedContextCodeString")} operation={LocalPatientOperations.MedfarBilling.ChangeContextAutres} disabled={false} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex-container" style={{ width: "270px", justifyContent: "flex-start" }}>
                    <div style={{ width: "39px" }}><label>Psycho.</label></div>
                    <RadioButton
                        items={[30, 45, 60, 75, 90, 105].map(x => new KeyValuePair(x, x)).toImmutable()}
                        radioItemParentStyle={{}}
                        radioInputStyle={{ marginLeft: "-13px", marginTop: "3px" }}
                        mainDivCSS=""
                        radioInputGroupId="_clientMedfarBillingRadioButton"
                        onChange={item => this.onPsychoRadioCheck(item.Value)}
                        selectedItem={this.props.Model.get("Psycho30") == true ? 30 : this.props.Model.get("Psycho45") == true ? 45 : this.props.Model.get("Psycho60") == true ? 60 : this.props.Model.get("Psycho75") == true ? 75 : this.props.Model.get("Psycho90") == true ? 90 : this.props.Model.get("Psycho105") == true ? 105 : 0}
                    />
                </div>

                {autreItemsRender}

                <h4 style={{ marginTop: "10px", marginBottom: "0px" }}>Éval.pour donner une opinion</h4>
                {evaluationRefereeRender}
            </div>
        );
    }

    private renderMidC(): JSX.Element {
        let csstRender = null;
        let csstList = this.props.Model.get("CSSTItems");
        if (csstList) {
            csstRender = Object.keys(csstList).map((key: string) => (
                <div className="checkbox" style={{ marginLeft: "5px", marginTop: "0px", marginBottom: "8px" }} key={key}>
                    <label>
                        <input type="checkbox" checked={csstList[key].IsChecked} onClick={() => this.onCSSTCheckboxClick(csstList[key])} />
                        {csstList[key].Description}
                    </label>
                </div>
            ));
        }

        return (
            <div>
                <h4 style={{ marginTop: "10px", marginBottom: "0px" }}>Autres codes</h4>
                <MedfarBillingAutoCompleteOtherCode
                    serverOperation={ServerOperations.Server.MedfarBilling_SearchOtherCode}
                    onItemSelectCallBack={item => this.onOtherCodeItemSelect(item)}
                    IsSpecialist={this.props.IsSpecialist}
                    ClearCodeOnSelect
                />

                <h4 style={{ marginTop: "10px", marginBottom: "0px" }}>CSST</h4>
                {csstRender}
            </div>
        );
    }

    private renderMidD(): JSX.Element {
        let communicationRender = null;
        let communicationList = this.props.Model.get("CommunicationsItems");
        if (communicationList && this.props.Model.get("IsMoreThan500")) {
            communicationRender = Object.keys(communicationList).map((key: string) => (
                <div className="checkbox" style={{ marginLeft: "5px", marginTop: "0px", marginBottom: "8px" }} key={key}>
                    <label>
                        <input type="checkbox" checked={communicationList[key].IsChecked} onClick={() => this.onCommunicationCheckboxClick(communicationList[key])} />
                        {communicationList[key].Description}
                    </label>
                </div>
            ));
        }

        let favoriteRender = null;
        let favoriteList = this.props.Model.get("FavoriteItems");
        if (favoriteList) {
            favoriteRender = Object.keys(favoriteList).map((key: string) => (
                <div className="checkbox flex-container" key={key}>
                    <div className="flex-container" style={{ justifyContent: "flex-start", marginLeft: "20px" }} title={"(" + favoriteList[key].Code + ") - " + favoriteList[key].Description}>
                        <div>
                            <input type="checkbox" checked={favoriteList[key].IsChecked} onClick={e => this.onFavoriteCheckboxClick(favoriteList[key], e)} />
                        </div>
                        <div style={{ justifyContent: "flex-start", whiteSpace: "nowrap", maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis" }} title={"(" + favoriteList[key].Code + ") - " + favoriteList[key].Description}>
                            {"(" + favoriteList[key].Code + ") - " + favoriteList[key].Description}
                        </div>
                    </div>
                    <div>
                        <span className="glyphicon glyphicon-remove red-text pointer" onClick={e => this.onRemoveItemFromFavoriteList(favoriteList[key], e)} />
                    </div>
                </div>
            ));
        }

        return (
            <div style={{ minWidth: "200px" }}>
                <h4 style={{ marginTop: "10px", marginBottom: "0px" }}>Communications</h4>
                {this.props.Model.get("IsLessThan500") && (
                    <div className="inline-block" style={{ marginRight: "8px" }}>
                        <label>
                            <input type="radio" name="s1" disabled checked={this.props.Model.get("MoreThan500Patient")} />
                            500+
                        </label>
                    </div>
                )}
                {this.props.Model.get("IsMoreThan500") && (
                    <div className="inline-block" style={{ marginRight: "8px" }}>
                        <label>
                            <input type="radio" name="s1" disabled={false} onClick={() => this.onMoreThan500PatientClick()} checked={this.props.Model.get("MoreThan500Patient")} />
                            500+
                        </label>
                    </div>
                )}
                {this.props.Model.get("IsLessThan500") && (
                    <div className="inline-block" style={{ marginRight: "8px" }}>
                        <label>
                            <input type="radio" name="s1" disabled onClick={() => { }} checked={this.props.Model.get("MoreThan1000Patient")} />
                            1000+
                        </label>
                    </div>
                )}
                {this.props.Model.get("IsMoreThan500") && (
                    <div className="inline-block" style={{ marginRight: "8px" }}>
                        <label>
                            <input type="radio" name="s1" disabled={false} onClick={() => this.onMoreThan1000PatientClick()} checked={this.props.Model.get("MoreThan1000Patient")} />
                            1000+
                        </label>
                    </div>
                )}
                {this.props.Model.get("IsLessThan500") && (
                    <div className="inline-block" style={{ marginRight: "8px" }}>
                        <label>
                            <input type="radio" name="s1" disabled onClick={() => { }} checked={this.props.Model.get("MoreThan1500Patient")} />
                            1500+
                        </label>
                    </div>
                )}
                {this.props.Model.get("IsMoreThan500") && (
                    <div className="inline-block" style={{ marginRight: "8px" }}>
                        <label>
                            <input type="radio" name="s1" disabled={false} onClick={() => this.onMoreThan1500PatientClick()} checked={this.props.Model.get("MoreThan1500Patient")} />
                            1500+
                        </label>
                    </div>
                )}

                {communicationRender}

                <h4 style={{ marginTop: "10px", marginBottom: "0px" }}>Favoris</h4>
                <div style={{ marginRight: "5px" }}>
                    {favoriteRender}
                </div>
                <a onClick={() => this.onAddFavoriteClick()}>Ajouter</a>
            </div>
        );
    }

    public componentDidMount(): void {
        $("#dttt01_BillDate").parent().css("width", "100px").parent().css("width", "125px");
        $("#time_001_BillTime").parent().css("width", "70px").parent().css("width", "70px");
        $("#time_002_BillIntClinTime").parent().css("width", "47px").parent().css("width", "47px");
        $("#ktype_").parent().css("width", "200px").parent().css("width", "200px");
    }

    public onCloseClick(): void {
        if (this.props.closeTarget)
            sessionStore.dispatch(this.props.closeTarget);
    }

    public onCommentsChange(value: string): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.CommentsChange, { value });
    }

    private onWithResidentClick(): void {
        this.mutateState(s => {
            const withResidentCurrentSelection: Person[] = s.withResidentCurrentSelection ? [s.withResidentCurrentSelection] : [];

            const AskResidentPopup: JSX.Element = (
                <MedfarBillingPopup width={560}>
                    <h2>Selectionner le Résident</h2>
                    <div className="flex-container" style={{ marginTop: "33px", justifyContent: "center" }}>
                        <div className="text-right" style={{ marginRight: "10px" }}>
                            <label style={{ marginTop: "7px" }}>Résident: </label>
                        </div>
                        <div style={{ width: "250px" }}>
                            <PersonSearchAutocompleteCustom
                                componentId="medfarBilling_selectResident"
                                searchStrategy={new ClinicianSearchStrategy(this.clinicians)}
                                selectedItemList={withResidentCurrentSelection.toImmutable()}
                                onSelectionCallback={(personItem: PersonDropdownItem) => this.handleClinicianChange(personItem)}
                                onClearCallback={(personItem: PersonDropdownItem) => this.handleClinicianChange(personItem)}
                                focusOnLoad
                                closeDropDownAfterSelection
                            />
                        </div>
                    </div>
                    <div className="flex-display flex-end" style={{ marginTop: "30px" }}>
                        <ButtonGroup>
                            <ButtonControl
                                className="btn btn-default"
                                label={cacheStore.GetString("CancelLabel")}
                                onClick={() => this.mutateState(s => s.popup = null)}
                            />
                            <ButtonControl
                                className="btn btn-primary"
                                label={cacheStore.GetString("ContinueLabel")}
                                onClick={() =>
                                    this.mutateState(s => {
                                        s.popup = null;
                                        s.AvecResident = this.state.withResidentCurrentSelection != null;
                                        s.AvecResidentLicence = this.state.withResidentCurrentSelection?.Licence;
                                        s.AvecResidentNom = this.state.withResidentCurrentSelection?.NameWithPrefix;
                                    })
                                }
                            />
                        </ButtonGroup>
                    </div>
                </MedfarBillingPopup>
            );
            s.popup = AskResidentPopup;
        });
    }

    private onDiagnosisItemSelect(item: DiagnosisResponse): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.AddDiagnosisToList, { value: item });
    }

    private onOtherCodeItemSelect(item: OtherCodeResponse): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.AddCodeToList, { value: item });
    }

    private onRemoveItemFromDiagnosis(item: IMedfarBillingCustomCodeItem): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.RemoveDiagnosisFromList, { value: item });
    }

    private onRemoveItemFromCodesList(item: FacturationCode): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.RemoveCodeToList, { value: item });
    }

    private onRemoveItemFromFavoriteList(item: FacturationCode, e: React.MouseEvent): void {
        e.stopPropagation();
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnFavoriteRemove, { value: item });
    }

    private onMoreThan500PatientClick(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnMoreThan500PatientClick);
    }

    private onMoreThan1000PatientClick(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnMoreThan1000PatientClick);
    }

    private onMoreThan1500PatientClick(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnMoreThan1500PatientClick);
    }

    private onRdvCheckBoxClick(item: FacturationCode): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnCheckboxClickRdv, { item });
    }

    private onGrossesseCheckboxClick(item: FacturationCode): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnCheckboxClickGrossesse, { item });
    }

    private onVADomicileCheckboxClick(item: FacturationCode): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnCheckboxVADomicileClick, { item });
    }

    private onSrvPatientNonInscritCheckboxClick(item: FacturationCode): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnCheckboxSrvNonInscritClick, { item });
    }

    private onAutreCheckboxClick(item: FacturationCode): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnCheckboxAutreClick, { item });
    }

    private onEvalReferreCheckboxClick(item: FacturationCode): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnEvalReferreCheckboxClick, { item });
    }

    private onCSSTCheckboxClick(item: FacturationCode): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnCheckboxCsstClick, { item });
    }

    private onCommunicationCheckboxClick(item: FacturationCode): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnCheckboxCommunicationClick, { item });
    }

    private onFavoriteCheckboxClick(item: FacturationCode, e: React.MouseEvent): void {
        e.stopPropagation();
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnCheckboxFavoriteClick, { item });
    }

    private onCancelButtonClick(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnCancelClick);
    }

    private onContinueButtonClick(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnContinueClick);
    }

    private removeRefDoctor(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.RemoveRefDoctor);
    }

    private removeCommSpecDoctor(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.RemoveCommSpecDoctor);
    }

    private removeCommProfDoctor(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.RemoveCommProfDoctor);
    }

    private onPsychoRadioCheck(value: number): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnPsychoRadioClick, { value });
    }

    private onIntClinChange(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnIntClinClick, { stateDTO: this.state });
    }

    private onCommProfFirstNameChange(value: string): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnCommProfFirstNameChange, { value });
    }

    private onCommProfLastNameChange(value: string): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnCommProfLastNameChange, { value });
    }

    private onCommProfOutQuebecClicked(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnCommProfOutQuebecClick);
    }

    private onCommProfQuebecClicked(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnCommProfQuebecClick);
    }

    private onPECRadioCheck(value: number): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnPecRadioCheck, { code: value });
    }

    private onAddFavoriteClick(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.AddFavoriteClick);
    }

    private onCancelFavouriteClick(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnCancelFavoriteClick);
    }

    private onCancelRDVorSRVClick(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnSrvOrRdvCancelClick);
    }

    private onMesure1TextChange(value: string): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnMesure1TextChange, { value });
    }
    private onMesure2TextChange(value: string): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.OnMesure2TextChange, { value });
    }

    private generateNewBill(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.GenerateBill, { stateDTO: this.state });
    }

    private onSceanceDiffClick(): void {
        sessionStore.dispatch(LocalPatientOperations.MedfarBilling.SceanceDiffClick);
    }

    public static getDefaultMedfarBillingModel(): IMedfarBillingModel {
        const contextList = [
            new ContextElementClass("-1", "Aucun"),
            new ContextElementClass("1714", "Problème de santé, vulnérable"),
            new ContextElementClass("1713", "Centre de pédiatrie sociale / Programme petite enfance"),
            new ContextElementClass("1715", "Santé Mentale"),
            new ContextElementClass("1716", "Toxicomanie")
        ];

        const pharmacist = new ContextElementClass("14", "Pharmacien");
        const typeList = [
            new ContextElementClass("1", "Médecin"),
            new ContextElementClass("3", "Infirmière praticienne spécialisée"),
            new ContextElementClass("4", "Travailleur social"),
            new ContextElementClass("5", "Ergothérapeute"),
            new ContextElementClass("6", "Sage-femme"),
            new ContextElementClass("7", "Physiothérapeute"),
            new ContextElementClass("8", "Psychologue"),
            new ContextElementClass("9", "Audiologiste"),
            new ContextElementClass("10", "Audioprothésiste"),
            new ContextElementClass("11", "Coroner"),
            new ContextElementClass("12", "Médecin de la commission"),
            new ContextElementClass("13", "Orthophoniste"),
            pharmacist,
            new ContextElementClass("15", "Médecin omnipracticien"),
            new ContextElementClass("16", "Médecin spécialiste"),
            new ContextElementClass("17", "Résident"),
            new ContextElementClass("18", "Dentiste"),
            new ContextElementClass("99", "Autre")
        ];

        // create empty model
        const medfarBillingModel: IJMedfarBillingModel = {
            Comments: "",
            BillDate: null,
            DiagnosisSearchText: "",
            DiagnosisList: [],
            CodesList: [],
            IsSRVChecked: false,
            IsRDVChecked: false,
            Is80YearsOld: false,
            IsPatientNotSubscribed: false,
            IsPatientSubscribed: false,
            IsLessThan500: false,
            IsMoreThan500: false,
            IsVulnerable: false,
            Model: null,
            MoreThan500Patient: false,
            MoreThan1000Patient: false,
            MoreThan1500Patient: false,

            DatePrevuAccouchement: null,
            DateCSST: null,
            TempDateCSST: null,
            TempDatePrevuAccouchement: null,
            MedecinReferant: null,
            ErrorsFacturation: [],
            IsDateErrorGrossesse: false,
            IsDateErrorCSST: false,
            IsRefDoctorError: false,
            IsCommDoctorError: false,
            IsCommProfessionalError: false,
            IsSuggestionAdditionError: false,
            IsNotVulnerableError: false,
            IsDoubleEvent: false,
            PopupInfoText: "",
            IsPopupInfoOpen: false,
            ReferalDoctorSearchString: "",
            SelectedReferalDoctorId: "",
            SelectedReferalDoctor: [],
            TempFacturationCode: null,
            ContextList: [],
            ContextStringList: [],
            SelectedContextCodeString: "",
            SelectedContextCode: null,
            Psycho30: false,
            Psycho45: false,
            Psycho60: false,
            Psycho75: false,
            Psycho90: false,
            Psycho105: false,
            IsCommSpecChecked: false,
            CommSpecErrorMessage: false,
            CommProfErrorMessage: false,
            IsCommProfChecked: false,
            CommSpecSearchString: "",
            SelectedCommSpecId: "",
            SelectedCommSpec: [],
            CommSpecDoctor: null,
            CommProfFirstName: "",
            CommProfLastName: "",
            CommProfTypeList: [],
            CommProfTypeStringList: [],
            CommProfTypeString: "",
            CommProfTypeSelected: null,
            CommProfOutQuebec: false,
            CommProfQuebec: false,
            ShowCommProf: false,
            MustHave500PatientError: false,
            PatientPECError: false,
            PatientVULError: false,
            Is19957CodeChecked: false,
            Is19958CodeChecked: false,
            Is19959CodeChecked: false,
            Is19960CodeChecked: false,
            FavoriteCode: "",
            FavoriteDescription: "",
            FavoritePopupOpen: false,
            Mesure1Error: false,
            Mesure2Error: false,
            NoSRVorRDVCheckedError: false,
            SceanceDiffChecked: false,
            IsUpdating: false,
            RdvPatientInscritItem: null,
            GrossesseItems: null,
            DomicilesItems: null,
            SrvNonInscritItem: null,
            AutresMultipleCbItems: null,
            AutresItems: null,
            EvaluationRefereeItems: null,
            CSSTItems: null,
            CommunicationsItems: null,
            FavoriteItems: null,
            IsConferenceCallChecked: false,
            TeleconferenceType: FacturationTeleconferenceType.Telephone
        };

        // [key] object must be set After to avoid item sharing the same reference
        return Immutable.fromJS(medfarBillingModel)
            .set("RdvPatientInscritItem", {})
            .set("GrossesseItems", {})
            .set("DomicilesItems", {})
            .set("SrvNonInscritItem", {})
            .set("AutresMultipleCbItems", {})
            .set("AutresItems", {})
            .set("EvaluationRefereeItems", {})
            .set("CSSTItems", {})
            .set("CommunicationsItems", {})
            .set("FavoriteItems", {})
            .set("ContextList", Immutable.List(contextList))
            .set("ContextStringList", Immutable.List(contextList.map(p => p.ContextDescription)))
            .set("CommProfTypeList", Immutable.List(typeList))
            .set("CommProfTypeStringList", Immutable.List(typeList.map(p => p.ContextDescription)))
            .set("CommProfTypeSelected", pharmacist)
            .set("CommProfQuebec", true);
    }

    private handleClinicianChange(personItem: PersonDropdownItem): void {
        const filtered = cacheStore.Clinicians.filter(x => x.ContactId == personItem.Item.ContactId);
        if (personItem == null || personItem.Item == null || filtered.length < 1)
            return;

        this.mutateState(s => s.withResidentCurrentSelection = filtered[0]);
    }

    private initData(): void {
        this.clinicians = cacheStore.Clinicians.filter(x => !IsNullOrWhiteSpace(x.Licence) && x.Licence.substr(0, 1) == "R");
    }
}

interface IMedfarBillingPopup {
    width: number;
}

class MedfarBillingPopup extends React.Component<PropsWithChildren<IMedfarBillingPopup>, never> {
    public render(): JSX.Element {
        return (
            <div className="action-popup" style={{ width: this.props.width + "px", marginLeft: ((this.props.width / 2) * -1) + "px" }}>
                {this.props.children}
            </div>
        );
    }
}

export { MedfarBilling, MedfarBillingState };
